<!-- components/UserManagement.vue -->
<template>
  <div>
    <!-- 空状态 -->
    <div v-if="userTree.length === 0" style="display:flex;flex-direction:column;align-items:center;padding:40px 0;">
      <var-icon name="account-circle" size="40" color="#c0c4cc" />
      <span style="margin-top:6px;color:var(--var-text-secondary);font-size:13px;">暂无用户</span>
    </div>

    <!-- 用户树形表格 -->
    <var-table v-else :scroller-height="'100%'" :elevation="0">
      <thead>
        <tr>
          <th>用户名</th>
          <th>权限</th>
          <th style="text-align:center;">操作</th>
        </tr>
      </thead>
      <tbody>
        <template v-for="item in userTree" :key="item.id">
          <!-- 主用户 / 生产 / 外雇行 -->
          <tr :style="{ backgroundColor: item.isMain ? '#f8f9fa' : 'transparent' }">
            <td>
              <div style="display:flex;align-items:center;gap:8px;">
                <var-icon :name="item.isOutsourced ? 'account-arrow-right' : 'account-circle'" size="18"
                  :color="item.isOutsourced ? '#67c23a' : '#409eff'" />
                <div style="display:flex;flex-direction:column;">
                  <span style="font-weight:500;">{{ item.username }}</span>
                  <div style="display:flex;align-items:center;gap:4px;flex-wrap:wrap;margin-top:2px;">
                    <var-badge v-if="item.role === 'admin'" type="danger" value="管理员" size="small" />
                    <var-badge v-if="item.isWorker" type="warning" value="生产人员" size="small" />
                    <var-badge v-if="item.isOutsourced" type="success" value="外雇" size="small" />
                    <var-badge
                      v-if="item.position_name && item.role !== 'admin' && !item.isWorker && !item.isOutsourced"
                      type="primary" :value="item.position_name" size="small" />
                    <span v-if="item.assistants && item.assistants.length > 0"
                      style="color:#909399;font-size:12px;margin-left:4px;">
                      ({{ item.assistants.length }} 个辅助)
                    </span>
                  </div>
                </div>
              </div>
            </td>
            <td>
              <var-space :size="4">
                <template v-if="item.main_user_id && item.main_user_id > 0">
                  <span style="color:#409eff;font-size:13px;">
                    继承自：{{ item.main_username || '未知主用户' }}
                  </span>
                  <span style="color:#909399;font-size:12px;">
                    ({{ (item.permissions || []).length }} 项)
                  </span>
                </template>
                <template v-else>
                  <span>{{ (item.permissions || []).length }} 项</span>
                </template>
              </var-space>
            </td>
            <td style="text-align:center;">
              <var-space :size="4">
                <var-badge v-if="item.username === appStore.currentUser" type="primary" value="我" />
                <var-button v-if="item.role !== 'admin' && item.username !== appStore.currentUser" size="small"
                  type="warning" plain @click="openUserForm(item)">
                  编辑
                </var-button>
                <!-- 主用户和生产人员可以添加辅助（外雇不能） -->
                <var-button
                  v-if="item.role !== 'admin' && item.username !== appStore.currentUser && !item.isOutsourced && (item.isWorker || item.isMain)"
                  size="small" type="success" plain @click="openAddAssistantForm(item)">
                  添加辅助
                </var-button>
                <var-button v-if="item.role !== 'admin' && item.username !== appStore.currentUser" size="small"
                  type="danger" plain @click="openDeleteUserDialog(item)">
                  删除
                </var-button>
              </var-space>
            </td>
          </tr>

          <!-- 辅助人员子行 -->
          <tr v-for="assistant in item.assistants" :key="assistant.id" style="background-color:#fafbfc;">
            <td>
              <div style="display:flex;align-items:center;gap:8px;padding-left:32px;">
                <var-icon name="account-child" size="18" color="#909399" />
                <div style="display:flex;flex-direction:column;">
                  <span style="color:#666;">{{ assistant.username }}</span>
                  <div style="display:flex;align-items:center;gap:4px;flex-wrap:wrap;margin-top:2px;">
                    <var-badge type="info" value="辅助" size="small" />
                    <var-badge v-if="assistant.position_name" type="primary" :value="assistant.position_name"
                      size="small" />
                  </div>
                </div>
              </div>
            </td>
            <td>
              <var-space :size="4">
                <template v-if="assistant.main_user_id && assistant.main_user_id > 0">
                  <span style="color:#409eff;font-size:13px;">
                    继承自：{{ assistant.main_username || '未知主用户' }}
                  </span>
                  <span style="color:#909399;font-size:12px;">
                    ({{ (assistant.permissions || []).length }} 项)
                  </span>
                </template>
                <template v-else>
                  <span>{{ (assistant.permissions || []).length }} 项</span>
                </template>
              </var-space>
            </td>
            <td style="text-align:center;">
              <var-space :size="4">
                <var-badge v-if="assistant.username === appStore.currentUser" type="primary" value="我" />
                <var-button v-if="assistant.username !== appStore.currentUser" size="small" type="warning" plain
                  @click="openUserForm(assistant)">
                  编辑
                </var-button>
                <var-button v-if="assistant.username !== appStore.currentUser" size="small" type="danger" plain
                  @click="openDeleteUserDialog(assistant)">
                  删除
                </var-button>
              </var-space>
            </td>
          </tr>
        </template>
      </tbody>
    </var-table>

    <!-- ===== 用户表单弹窗 ===== -->
    <var-dialog v-model:show="showUserForm" :title="userFormMode === 'edit' ? '✏️ 编辑用户' : '➕ 添加用户'" width="620px"
      @confirm="handleUserSubmit" @close="handleDialogClose" :confirm-loading="submitLoading">
      <div style="padding:8px 0;">
        <!-- 用户名 + 用户类型 -->
        <div style="display:flex;gap:16px;margin-bottom:12px;">
          <div style="flex:1;display:flex;flex-direction:column;gap:4px;">
            <span style="font-size:13px;color:#666;font-weight:500;">
              用户名 <span style="color:#f56c6c;">*</span>
              <span v-if="userForm.userType === 'outsourced' && userFormMode === 'edit'"
                style="color:#999;font-weight:400;margin-left:4px;">（外雇不可改名）</span>
            </span>
            <var-input v-model="userForm.username" placeholder="请输入用户名"
              :disabled="userForm.userType === 'outsourced' && userFormMode === 'edit'" />
          </div>
          <div style="flex:1;display:flex;flex-direction:column;gap:4px;">
            <span style="font-size:13px;color:#666;font-weight:500;">用户类型</span>
            <var-select v-model="userForm.userType" :options="userTypeOptions" placeholder="请选择用户类型"
              @change="handleUserTypeChange" />
            <span v-if="showCannotDowngradeWarning" style="color:#f56c6c;font-size:12px;margin-top:2px;">
              ⚠️ 该用户有 {{ userForm.assistantCount }} 个辅助人员，不能降级为辅助人员，请先删除所有辅助人员
            </span>
          </div>
        </div>

        <!-- 辅助人员关联 -->
        <div v-if="userForm.userType === 'assistant'" style="display:flex;gap:16px;margin-bottom:12px;">
          <div style="flex:1;display:flex;flex-direction:column;gap:4px;">
            <span style="font-size:13px;color:#666;font-weight:500;">关联主用户 <span style="color:#f56c6c;">*</span></span>
            <var-select v-model="userForm.main_user_id" :options="mainUserOptions" placeholder="请选择主用户"
              @change="handleMainUserChange" />
            <span v-if="mainUserOptions.length === 0" style="color:#f56c6c;font-size:12px;margin-top:4px;">
              ⚠️ 暂无可用主用户，请先创建主用户
            </span>
          </div>
        </div>

        <!-- 职务选择（辅助/外雇不显示） -->
        <div v-if="userForm.userType !== 'assistant' && userForm.userType !== 'outsourced'"
          style="display:flex;gap:16px;margin-bottom:12px;">
          <div style="flex:1;display:flex;flex-direction:column;gap:4px;">
            <span style="font-size:13px;color:#666;font-weight:500;">职务</span>
            <var-select v-model="userForm.position_id" :options="positionOptions" placeholder="请选择职位"
              @change="handlePositionChange" />
          </div>
        </div>

        <!-- 密码区域（外雇不显示） -->
        <div v-if="userForm.userType !== 'outsourced'"
          style="background:#f7f9fc;border-radius:6px;padding:12px 16px;margin-bottom:12px;">
          <div style="display:flex;align-items:center;justify-content:space-between;">
            <span style="font-size:13px;color:#666;">
              <template v-if="userForm.useDefaultPassword">
                🔑 默认密码 (12345678)
              </template>
              <template v-else>
                ✏️ 自定义密码
              </template>
            </span>
            <var-switch v-model="userForm.useDefaultPassword" :true-value="true" :false-value="false" size="18px">
              <template #true>默认</template>
              <template #false>自定义</template>
            </var-switch>
          </div>

          <template v-if="!userForm.useDefaultPassword">
            <div style="display:flex;gap:16px;margin-top:8px;">
              <div style="flex:1;">
                <var-input v-model="userForm.password" type="password" placeholder="新密码（至少6位）" />
              </div>
              <div style="flex:1;">
                <var-input v-model="userForm.confirmPassword" type="password" placeholder="确认密码" />
              </div>
            </div>
          </template>
        </div>

        <!-- ✅ 外雇人员提示 -->
        <div v-if="userForm.userType === 'outsourced'"
          style="background:#f0f9eb;border-radius:6px;padding:12px 16px;margin-bottom:12px;">
          <div style="display:flex;align-items:center;gap:8px;">
            <var-icon name="information" size="20" color="#67c23a" />
            <span style="color:#67c23a;font-size:13px;">
              外雇人员可登录系统查看自己的账目。请在下方的权限树中勾选「📒 记账管理」相关权限。
            </span>
          </div>
        </div>

        <!-- 权限树（辅助人员不显示，外雇显示） -->
        <div v-if="userForm.userType !== 'assistant'"
          style="border:1px solid #e8e8e8;border-radius:6px;overflow:hidden;">
          <div style="padding:6px 14px;background:#f7f9fc;border-bottom:1px solid #e8e8e8;">
            <span style="font-size:13px;color:#666;font-weight:500;">权限设置</span>
          </div>
          <div style="padding:4px 8px;">
            <PermissionTreeSelect ref="permissionTreeRef" v-model="userForm.permissions" mode="normal"
              :disabled="isEditingSelf" />
          </div>
          <div v-if="isEditingSelf" style="padding:4px 12px 8px 12px;color:#f56c6c;font-size:12px;">
            ⚠️ 不能修改自己的权限
          </div>
        </div>

        <!-- 辅助人员：显示继承信息 -->
        <div v-if="userForm.userType === 'assistant'"
          style="background:#ecf5ff;border-radius:6px;padding:12px 16px;margin-top:8px;">
          <div style="display:flex;align-items:center;gap:8px;">
            <var-icon name="information" size="20" color="#409eff" />
            <span style="color:#409eff;font-size:13px;">
              该用户为辅助人员，权限继承自：
              <strong>{{ getMainUsername(userForm.main_user_id) }}</strong>
            </span>
          </div>
        </div>
      </div>
    </var-dialog>

    <!-- ===== 添加辅助人员快捷弹窗 ===== -->
    <var-dialog v-model:show="showAddAssistantDialog" title="➕ 添加辅助人员" width="480px" @confirm="handleAddAssistantSubmit"
      :confirm-loading="submitLoading">
      <div style="padding:8px 0;">
        <div style="display:flex;flex-direction:column;gap:4px;margin-bottom:12px;">
          <span style="font-size:13px;color:#666;font-weight:500;">用户名 <span style="color:#f56c6c;">*</span></span>
          <var-input v-model="newAssistant.username" placeholder="请输入辅助人员用户名" />
        </div>
        <div style="display:flex;flex-direction:column;gap:4px;margin-bottom:12px;">
          <span style="font-size:13px;color:#666;font-weight:500;">关联主用户</span>
          <span style="color:#409eff;font-size:14px;font-weight:500;">{{ addAssistantTarget?.username }}</span>
        </div>
        <div style="background:#ecf5ff;border-radius:6px;padding:12px 16px;">
          <div style="display:flex;align-items:center;gap:8px;">
            <var-icon name="information" size="20" color="#409eff" />
            <span style="color:#409eff;font-size:13px;">
              辅助人员将继承 "{{ addAssistantTarget?.username }}" 的所有权限
            </span>
          </div>
        </div>
      </div>
    </var-dialog>

    <!-- ===== 确认删除弹窗 ===== -->
    <var-dialog v-model:show="showDeleteUserDialog" title="⚠️ 确认删除" width="400px" @confirm="handleDeleteUserConfirm">
      <div style="padding:8px 0;">
        <p style="margin-bottom:8px;">
          确定要删除用户 <strong>{{ deleteUserTarget?.username }}</strong> 吗？
        </p>
        <p v-if="deleteUserTarget?.isOutsourced" style="color:#e6a23c;font-size:13px;">
          💡 该外雇人员在记账中的历史记录仍会保留
        </p>
        <p style="color:#f56c6c;font-size:13px;">
          ⚠️ 此操作不可恢复！
        </p>
      </div>
    </var-dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, nextTick } from 'vue'
import { Snackbar } from '@varlet/ui'
import { useAppStore } from '@/stores'
import { userApi } from '@/api'
import PermissionTreeSelect from '@/components/common/PermissionTreeSelect.vue'

const appStore = useAppStore()

// ===== State =====
const users = ref([])
const positions = ref([])
const permissionTreeRef = ref(null)
const submitLoading = ref(false)

// ============================================================
// ✅ 用户类型选项（含外雇）
// ============================================================
const userTypeOptions = [
  { label: '主用户', value: 'normal' },
  { label: '辅助人员', value: 'assistant' },
  { label: '生产人员', value: 'worker' },
  { label: '外雇人员', value: 'outsourced' }   // ✅ 新增
]

// ===== Computed =====
const positionOptions = computed(() => {
  return positions.value.map(p => ({
    label: p.name + `（${(p.permissions || []).length}项）`,
    value: String(p.id),
  }))
})

// ============================================================
// ✅ 主用户选项（主用户和生产人员都可以被选为主用户）
// ============================================================
const mainUserOptions = computed(() => {
  const excludeUsername = userForm.value.originalUsername || userForm.value.username

  const mainUsers = users.value.filter(u =>
    (u.user_type === 'main' || u.user_type === 'worker') &&
    u.role !== 'admin' &&
    u.username !== appStore.currentUser &&
    u.username !== excludeUsername
  )

  return mainUsers.map(u => ({
    label: u.username + (u.position_name ? `（${u.position_name}）` : '') + (u.user_type === 'worker' ? ' 👷' : ''),
    value: String(u.id),
  }))
})

// ============================================================
// ✅ 用户树形结构（含外雇）
// ============================================================
const userTree = computed(() => {
  const mainUserList = users.value.filter(u => u.user_type === 'main' || u.user_type === 'worker')
  const outsourcedList = users.value.filter(u => u.user_type === 'outsourced')
  const assistantList = users.value.filter(u => u.user_type === 'assistant')

  const tree = mainUserList.map(mainUser => {
    const assistants = assistantList.filter(a => a.main_user_id === mainUser.id)
    return {
      ...mainUser,
      isMain: true,
      isWorker: mainUser.user_type === 'worker',
      isOutsourced: false,
      assistants: assistants.map(a => ({
        ...a,
        isMain: false,
        isWorker: false,
        isOutsourced: false
      }))
    }
  })

  // ✅ 外雇人员单独列出
  const outsourcedTree = outsourcedList.map(u => ({
    ...u,
    isMain: false,
    isWorker: false,
    isOutsourced: true,
    assistants: []
  }))

  return [...tree, ...outsourcedTree]
})

// ===== 用户表单 =====
const showUserForm = ref(false)
const userFormMode = ref('add')
const userForm = ref({
  username: '',
  password: '',
  confirmPassword: '',
  useDefaultPassword: true,
  position_id: '',
  permissions: [],
  originalUsername: '',
  userType: 'normal',
  main_user_id: null,
  assistantCount: 0,
})

// ============================================================
// ✅ 显示降级警告
// ============================================================
const showCannotDowngradeWarning = computed(() => {
  return userFormMode.value === 'edit' &&
    userForm.value.assistantCount > 0 &&
    userForm.value.userType === 'assistant'
})

// ===== 删除 =====
const showDeleteUserDialog = ref(false)
const deleteUserTarget = ref(null)

// ===== 添加辅助 =====
const showAddAssistantDialog = ref(false)
const addAssistantTarget = ref(null)
const newAssistant = ref({ username: '' })

// ===== 获取主用户名 =====
function getMainUsername(mainUserId) {
  if (!mainUserId) return '未设置'
  const mainUser = users.value.find(u => u.id === parseInt(mainUserId))
  return mainUser?.username || '未知用户'
}

// ===== 判断是否在编辑自己 =====
const isEditingSelf = computed(() => {
  return userFormMode.value === 'edit' && userForm.value.originalUsername === appStore.currentUser
})

// ===== 加载数据 =====
async function loadUsers() {
  try {
    const data = await userApi.getUsers()
    if (data?.success) {
      users.value = data.users || []
    }
  } catch (err) {
    console.error('[UserManagement] 加载用户失败:', err)
    Snackbar.error('加载用户失败')
  }
}

async function loadPositions() {
  try {
    const data = await userApi.getPositions()
    if (data?.success) {
      positions.value = data.data || []
    }
  } catch (err) {
    console.error('[UserManagement] 加载职位失败:', err)
    Snackbar.error('加载职位失败')
  }
}

// ============================================================
// ✅ 用户类型变化
// ============================================================
const handleUserTypeChange = (type) => {
  if (userForm.value.assistantCount > 0 && type === 'assistant' && userFormMode.value === 'edit') {
    Snackbar.warning(`该用户有 ${userForm.value.assistantCount} 个辅助人员，不能降级为辅助人员，请先删除所有辅助人员`)
    return
  }

  if (type === 'assistant') {
    userForm.value.permissions = []
    userForm.value.position_id = ''
    if (mainUserOptions.value.length > 0) {
      userForm.value.main_user_id = mainUserOptions.value[0].value
    } else {
      userForm.value.main_user_id = null
    }
  } else {
    userForm.value.main_user_id = null
    if (type === 'outsourced') {
      userForm.value.position_id = ''
    }
  }
}

// ===== 主用户变化 =====
const handleMainUserChange = (mainUserId) => {
  if (!mainUserId || userForm.value.userType !== 'assistant') return
  const mainUser = users.value.find(u => String(u.id) === String(mainUserId))
  if (mainUser) {
    Snackbar.info(`将继承 "${mainUser.username}" 的权限`)
  }
}

// ===== 职务变化 =====
const handlePositionChange = (positionId) => {
  if (!positionId) {
    userForm.value.permissions = []
    permissionTreeRef.value?.setSelectedValues([])
    return
  }

  const selectedPosition = positions.value.find(p => String(p.id) === String(positionId))
  if (selectedPosition && selectedPosition.permissions) {
    const positionPermissions = selectedPosition.permissions || []
    userForm.value.permissions = [...positionPermissions]
    permissionTreeRef.value?.setSelectedValues(positionPermissions)
    Snackbar.success(`已切换到"${selectedPosition.name}"，权限已自动更新`)
  }
}

// ===== 打开用户表单 =====
function openUserForm(user = null) {
  if (user) {
    userFormMode.value = 'edit'
    let userType = 'normal'

    if (user.user_type === 'worker') {
      userType = 'worker'
    } else if (user.user_type === 'assistant') {
      userType = 'assistant'
    } else if (user.user_type === 'outsourced') {   // ✅ 识别外雇
      userType = 'outsourced'
    } else {
      userType = 'normal'
    }

    const isAssist = userType === 'assistant'
    const isOutsourced = userType === 'outsourced'
    const assistantCount = userTree.value.find(u => u.id === user.id)?.assistants?.length || 0

    userForm.value = {
      username: user.username,
      password: '',
      confirmPassword: '',
      useDefaultPassword: true,
      position_id: (isAssist || isOutsourced) ? '' : (user.position_id ? String(user.position_id) : ''),
      permissions: isAssist ? [] : (user.permissions || []),
      originalUsername: user.username,
      userType: userType,
      main_user_id: isAssist ? String(user.main_user_id) : null,
      assistantCount: assistantCount,
    }
  } else {
    userFormMode.value = 'add'
    userForm.value = {
      username: '',
      password: '',
      confirmPassword: '',
      useDefaultPassword: true,
      position_id: '',
      permissions: [],
      originalUsername: '',
      userType: 'normal',
      main_user_id: null,
      assistantCount: 0,
    }
    if (mainUserOptions.value.length > 0) {
      userForm.value.main_user_id = mainUserOptions.value[0].value
    }
  }
  showUserForm.value = true

  nextTick(() => {
    if (permissionTreeRef.value && userForm.value.permissions.length > 0) {
      permissionTreeRef.value.setSelectedValues(userForm.value.permissions)
    }
  })
}

// ===== 弹窗关闭 =====
const handleDialogClose = () => {
  userForm.value = {
    username: '',
    password: '',
    confirmPassword: '',
    useDefaultPassword: true,
    position_id: '',
    permissions: [],
    originalUsername: '',
    userType: 'normal',
    main_user_id: null,
    assistantCount: 0,
  }
}

// ============================================================
// ✅ 提交用户表单
// ============================================================
async function handleUserSubmit() {
  if (!userForm.value.username?.trim()) {
    Snackbar.error('请输入用户名')
    return
  }

  if (userForm.value.assistantCount > 0 && userForm.value.userType === 'assistant') {
    Snackbar.warning(`该用户有 ${userForm.value.assistantCount} 个辅助人员，不能降级为辅助人员，请先删除所有辅助人员`)
    return
  }

  if (userForm.value.userType === 'assistant' && !userForm.value.main_user_id) {
    Snackbar.error('请选择关联的主用户')
    return
  }

  // 外雇不需要密码
  if (userForm.value.userType !== 'outsourced' && !userForm.value.useDefaultPassword) {
    if (!userForm.value.password || userForm.value.password.length < 6) {
      Snackbar.error('请输入至少6位密码')
      return
    }
    if (userForm.value.password !== userForm.value.confirmPassword) {
      Snackbar.error('两次输入的密码不一致')
      return
    }
  }

  // ============================================================
  // 1. 确定 user_type
  // ============================================================
  let userType = 'main'
  if (userForm.value.userType === 'assistant') {
    userType = 'assistant'
  } else if (userForm.value.userType === 'worker') {
    userType = 'worker'
  } else if (userForm.value.userType === 'outsourced') {   // ✅
    userType = 'outsourced'
  }

  // ============================================================
  // 2. 确定 main_user_id
  // ============================================================
  let mainUserId = null
  if (userForm.value.userType === 'assistant') {
    mainUserId = parseInt(userForm.value.main_user_id)
  }

  // ============================================================
  // 3. 确定 permissions（权限管理控制）
  // ============================================================
  let finalPermissions = userForm.value.permissions || []

  if (userForm.value.userType === 'assistant') {
    finalPermissions = []
  }

  if (isEditingSelf.value) {
    const currentUser = users.value.find(u => u.username === appStore.currentUser)
    finalPermissions = currentUser?.permissions || []
  }

  // ============================================================
  // 4. 确定 position_id
  // ============================================================
  let positionId = userForm.value.position_id || null
  if (userForm.value.userType === 'assistant' || userForm.value.userType === 'outsourced') {
    positionId = null
  }

  // ============================================================
  // 5. 构建请求数据
  // ============================================================
  const data = {
    username: userForm.value.username.trim(),
    permissions: finalPermissions,
    position_id: positionId,
    main_user_id: mainUserId,
    user_type: userType,
  }

  if (userFormMode.value === 'add') {
    if (userForm.value.userType === 'outsourced') {
      data.password = ''   // 外雇无密码
    } else {
      data.password = userForm.value.useDefaultPassword ? '12345678' : userForm.value.password
    }
  } else if (userForm.value.userType !== 'outsourced' && !userForm.value.useDefaultPassword && userForm.value.password) {
    data.password = userForm.value.password
  }

  submitLoading.value = true

  try {
    let result
    if (userFormMode.value === 'edit') {
      result = await userApi.updateUser(userForm.value.originalUsername, {
        ...data,
        newUsername: data.username,
      })
    } else {
      result = await userApi.addUser(
        data.username,
        data.password,
        data.permissions,
        data.position_id,
        data.main_user_id,
        data.user_type
      )
    }

    if (result?.success) {
      Snackbar.success(userFormMode.value === 'edit' ? '用户更新成功' : '用户创建成功')
      showUserForm.value = false
      await loadUsers()
    } else {
      Snackbar.error(result?.msg || '操作失败')
    }
  } catch (err) {
    console.error('[UserManagement] 用户操作失败:', err)
    Snackbar.error('操作失败: ' + err.message)
  } finally {
    submitLoading.value = false
  }
}

// ===== 添加辅助人员 =====
function openAddAssistantForm(user) {
  addAssistantTarget.value = user
  newAssistant.value.username = ''
  showAddAssistantDialog.value = true
}

async function handleAddAssistantSubmit() {
  if (!newAssistant.value.username?.trim()) {
    Snackbar.error('请输入用户名')
    return
  }

  submitLoading.value = true

  try {
    const result = await userApi.addUser(
      newAssistant.value.username.trim(),
      '12345678',
      [],
      null,
      addAssistantTarget.value.id,
      'assistant'
    )

    if (result?.success) {
      Snackbar.success(`辅助人员 "${newAssistant.value.username}" 添加成功`)
      showAddAssistantDialog.value = false
      await loadUsers()
    } else {
      Snackbar.error(result?.msg || '添加失败')
    }
  } catch (err) {
    console.error('[UserManagement] 添加辅助人员失败:', err)
    Snackbar.error('添加失败: ' + err.message)
  } finally {
    submitLoading.value = false
  }
}

// ===== 删除用户 =====
function openDeleteUserDialog(user) {
  deleteUserTarget.value = user
  showDeleteUserDialog.value = true
}

async function handleDeleteUserConfirm() {
  if (!deleteUserTarget.value) return

  submitLoading.value = true

  try {
    const result = await userApi.deleteUser(deleteUserTarget.value.username)
    if (result?.success) {
      Snackbar.success('用户已删除')
      showDeleteUserDialog.value = false
      deleteUserTarget.value = null
      await loadUsers()
    } else {
      Snackbar.error(result?.msg || '删除失败')
    }
  } catch (err) {
    console.error('[UserManagement] 删除用户失败:', err)
    Snackbar.error('删除失败: ' + err.message)
  } finally {
    submitLoading.value = false
  }
}

// ===== 暴露给父组件 =====
defineExpose({
  loadUsers,
  loadPositions,
  openUserForm,
})

// ===== 初始化 =====
onMounted(() => {
  loadUsers()
  loadPositions()
})
</script>